import { withRouter, WithRouterProps } from 'react-router';
import BemHelper from '@flowio/bem-helper';
import React, { PureComponent } from 'react';
import { Nav, NavLink } from '@flowio/react-nav';
import createRoutesFactory from '../../utilities/createRoutesFactory';
import { getZigZagReturnsUrl, getZigZagReturnsPortal } from '../../../../hacks/get-zig-zag-returns-url';
import { LeftNavigationProps } from '../../types/props';
import canViewPage from '../../utilities/canViewPage';
import { hasOnlyChannelOrgAdminRoles } from '../../utilities/is-channel-organization-admin-user';
import { hasOnlyFulfillmentRole } from '../../utilities/isFulfillmentUser';
import { hasOnlyClassificationRoles } from '../../utilities/is-classification-user';

if (process.browser) {
  require('./left-navigation.css'); // eslint-disable-line global-require
}

const bem = new BemHelper('left-navigation');

export class LeftNavigation extends PureComponent<LeftNavigationProps & WithRouterProps> {
  static displayName = 'LeftNavigation';

  static defaultProps = {
    isGlobalFulfillmentConsoleEnabled: false,
    isGlobalPendingMembersEnabled: false,
    isGlobalConsoleReturnsEnabled: false,
    orgRoles: [],
  };

  render(): React.ReactElement {
    const {
      orgRoles = [],
      isGlobalPendingMembersEnabled,
      isGlobalFulfillmentConsoleEnabled,
      isGlobalConsoleReturnsEnabled,
      organization,
      router,
    } = this.props;

    const isChannelOrgAdmin = hasOnlyChannelOrgAdminRoles(orgRoles);
    const isFulfillmentUser = hasOnlyFulfillmentRole(orgRoles);
    const isClassificationUser = hasOnlyClassificationRoles(orgRoles);

    const createRoutes = createRoutesFactory(router);

    const classificationUserLinks = isClassificationUser ? createRoutes([
      {
        text: 'Items',
        icon: 'Book',
        href: `/${organization}/catalog/items`,
      },
    ]) : null;

    const channelOrgAdminLinks = isChannelOrgAdmin ? createRoutes([
      {
        text: 'Billing',
        icon: 'Paper',
        href: `/${organization}/organization/accounts`,
      },
      {
        text: 'Members',
        icon: 'UserCircle',
        href: `/${organization}/organization/members`,
      },
      {
        text: 'Fulfillment',
        icon: 'Fulfillment',
        href: `/${organization}/fulfillment`,
      },
    ]) : null;

    const fulfillmentUserLinks = isFulfillmentUser ? createRoutes([
      {
        text: 'Fulfillment',
        icon: 'Fulfillment',
        href: `/${organization}/fulfillment`,
      },
    ]) : null;

    const defaultLinks = createRoutes([{
      defaultExpanded: router.isActive(`/${organization}/catalog`, false),
      icon: 'Book',
      text: 'Product Catalog',
      links: [{
        href: `/${organization}/catalog/items`,
        disabled: !canViewPage(`/${organization}/catalog/items`, orgRoles),
        text: 'Items',
      }],
      disabled: !canViewPage(`/${organization}/catalog`, orgRoles),
    }, {
      defaultExpanded: router.isActive(`/${organization}/classification`, false),
      icon: 'Harmonization',
      text: 'Classification',
      href: `/${organization}/classification`,
      disabled: !canViewPage(`/${organization}/classification`, orgRoles),
    }, {
      disabled: !isGlobalConsoleReturnsEnabled
        || !canViewPage(getZigZagReturnsUrl(organization), orgRoles)
        || !canViewPage(getZigZagReturnsPortal(organization), orgRoles),
      icon: 'PackageArrows',
      text: 'Returns',
      links: [{
        href: getZigZagReturnsUrl(organization),
        text: 'Manage Returns',
        rel: 'noopener noreferrer',
      }, {
        href: getZigZagReturnsPortal(organization),
        rel: 'noopener noreferrer',
        text: 'Returns Portal',
      }],
    }, {
      disabled: !isGlobalFulfillmentConsoleEnabled || !canViewPage(`/${organization}/fulfillment`, orgRoles),
      href: `/${organization}/fulfillment`,
      text: 'Fulfillment',
      icon: 'Fulfillment',
    },
    {
      disabled: !canViewPage(`/${organization}/orders/search`, orgRoles),
      href: `/${organization}/orders/search`,
      icon: 'Headset',
      text: 'Customer Service',
    }, {
      href: `/${organization}/analytics`,
      icon: 'Chart',
      text: 'Analytics',
      disabled: !canViewPage(`/${organization}/analytics`, orgRoles),
    }, {
      defaultExpanded: router.isActive(`/${organization}/utilities`, false),
      icon: 'Wrench',
      text: 'Utility & Tools',
      links: [{
        href: `/${organization}/utilities/rates`,
        disabled: !canViewPage(`/${organization}/utilities/rates`, orgRoles),
        text: 'Currency Rates',
      },
      ],
    }, {
      disabled: !isGlobalPendingMembersEnabled || !canViewPage(`/${organization}/pending`, orgRoles),
      icon: 'CircleCheck',
      text: 'Pending Members',
      href: `/${organization}/pending`,
    },
    {
      defaultExpanded: router.isActive(`/${organization}/organization`, false),
      icon: 'Organization',
      text: 'Organization Settings',
      disabled: !canViewPage(`/${organization}/organization`, orgRoles),
      links: [{
        href: `/${organization}/organization/members`,
        text: 'Members',
      }, {
        href: `/${organization}/organization/features`,
        text: 'Features',
      }, {
        href: `/${organization}/organization/integrations`,
        text: 'Integrations',
      }, {
        href: `/${organization}/organization/accounts`,
        text: 'Billing',
      }, {
        href: `/${organization}/organization/policies`,
        text: 'Policies',
      }],
    }]);

    return (
      <div className={bem.block()}>
        <Nav>
          {fulfillmentUserLinks || classificationUserLinks || channelOrgAdminLinks || defaultLinks}
        </Nav>

        { !isChannelOrgAdmin && !isFulfillmentUser && !isClassificationUser && (
          <div className={bem.element('api-reference')}>
            <NavLink href="https://docs.flow.io" text="API Reference" />
          </div>
        )}
      </div>
    );
  }
}

export default withRouter<LeftNavigationProps>(LeftNavigation);
